import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Footer from './components/base/Footer';
import { AuthProvider } from './contexts/AuthContext';
import { BrokerConnectionsProvider } from './providers/BrokerConnectionsProvider';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationHelper from './components/notifications/NotificationHelper';
import ProtectedRoute from './components/auth/ProtectedRoute';
import OAuthProtectedRoute from './components/auth/OAuthProtectedRoute';
import BrokerConnectionGuard from './components/auth/BrokerConnectionGuard';
import PaymentVerificationGuard from './components/auth/PaymentVerificationGuard';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';

// ✅ Import your components
import BrokerConnectPage from './pages/broker-connect';
import AccessRequest from './pages/access-request';
import UserDashboard from './pages/picking-broker';
import DepositPage from './pages/deposit';
import DashboardPage from './pages/dashboard';
import BotPage from './pages/bot-pages/BotPage';
import MainPageSch from './pages/pages-sch';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import SignUpPage from './pages/auth/SignUpPage';
import ForgotPassword from './pages/auth/ForgotPassword';
import TwoFactorVerifyPage from './pages/auth/TwoFactorVerifyPage';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

// ✅ Import additional pages from the correct path
import UpgradePage from './pages/upgrade/UpgradePage';
import ProfilePage from './pages/profile/ProfilePage';
import AccountPage from './pages/account';
import TermsAndConditions from './pages/sch-web/components/TermsAndConditions';  // Correct path for TermsAndConditions
//import AccountLinking from './pages/sch-web/components/AccountLinking';  // Correct path for AccountLinking
import WatchListPage from './pages/watchlist';
import OldLoginPage from './pages/login';
import HomePage from './pages/home';
import WhyQuantumFloPage from './pages/why-quantum';
import SolutionsPage from './pages/solutions';
import HowItWorksPage from './pages/how-it-works';
import PricingPage from './pages/pricing';
import CompanyPage from './pages/company';

import TradeHistoryPage from './pages/trade-history';
import HistoryPage from './pages/notifications/HistoryPage';

import Options101Page from './pages/options-101';

import WhatIsMarketMakingPage from './pages/what-is-market-making'
import HowToPlaceAnOptionsTradePage from './pages/how-to-place-an-options-trade'
import PositionSizeAndRiskManagementPage from './pages/position-size-and-risk-management'
import PatternDayTraderRulePage from './pages/pattern-day-trader-rule'
import HowToHandleOptionsAssignmentPage from './pages/how-to-handle-options-assignment'
import DisclaimerPage from './pages/legal/Disclaimer';
import LegalTermsAndConditionsPage from './pages/legal/LegalTermsAndConditions';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicy';
import RefundAndCancellationPage from './pages/legal/RefundAndCancellation';

import WhatAreOptions from './pages/options/WhatAreOptions';
import TypesOfOptions from './pages/options/TypesOfOptions';
import OptionsStrategies from './pages/options/OptionsStrategies';
import LearnPage from './pages/learn';
import QuantumFloSignalsPage from './pages/quantum-flo-signals';
import Home2Page from './pages/deposit/home2';
import ThankYou from './pages/login/thank-you';
import ThankYouBooking from './pages/thank-you-booking';
import SchwabComingSoon from './pages/broker-connect/schwab-coming-soon';

// Broker Integration
import BrokerIntegrationsPage from './pages/broker/BrokerIntegrationsPage';
import BrokerCallbackPage from './pages/broker/BrokerCallbackPage';
import AccountSelectionPage from './pages/account-selection';

// Admin
import AdminDashboard from './components/admin/AdminDashboard';
import AdminPage from './components/admin/AdminPage';
import AdminClientTradeHistory from './components/admin/AdminClientTradeHistory';
import AdminClientPerformance from './components/admin/AdminClientPerformance';

// Error pages
import NotFoundPage from './pages/errors/NotFoundPage';
import MaintenancePage from './pages/errors/MaintenancePage';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrokerConnectionsProvider>
          <NotificationProvider>
            <NotificationHelper />
            <Router>
              <ScrollToTop />
              <div className="min-h-screen flex flex-col bg-[#0b0d2a] overflow-x-hidden">
                <main className="flex-grow w-full pb-20">
                  <Routes>                  
                  {/* ✅ Authentication and Login */}
                  <Route path="/" element={<Home2Page />} />
                  <Route path="/why-quantum" element={<WhyQuantumFloPage />} />
                  <Route path="/solutions" element={<SolutionsPage />} />
                  <Route path="/how-it-works" element={<HowItWorksPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/company" element={<CompanyPage />} />

                  <Route path="/login" element={<GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}><LoginPage /></GoogleReCaptchaProvider>} />
                  {/* Signup stays locked unless the URL carries a valid purchase_key
                      (post-purchase email). SignUpPage enforces the unlock; POST /signup
                      is gated the same way in functions-ts. */}
                  <Route path="/signup" element={<GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}><SignUpPage /></GoogleReCaptchaProvider>} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/two-factor-verify" element={<TwoFactorVerifyPage />} />
                  <Route path="/old-login" element={<OldLoginPage />} />  {/* Old LoginPage */}
                  <Route path="/presale" element={<Navigate to="/pricing" replace />} />
                  <Route path="/usersignup" element={<Navigate to="/login" replace />} />
                  <Route path="/broker-connect" element={<BrokerConnectPage />} />  {/* Broker connection page */}
                  <Route path="/broker-connect/schwab-coming-soon" element={<SchwabComingSoon />} />  {/* Schwab Coming Soon page */}
                  
                  {/* Additional public pages */}
                  <Route path="/home2" element={<Home2Page />} />
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/thank-you-booking" element={<ThankYouBooking />} />

                  {/* ✅ Access Request & Success Pages */}
                  <Route path="/access-request" element={<AccessRequest />} />

                  
                  {/* ✅ Dashboard & Picking Broker Pages */}
                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <BrokerConnectionGuard>
                          <DashboardPage />
                        </BrokerConnectionGuard>
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/picking-broker" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <UserDashboard />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  
                  {/* ✅ Deposit and Bot Pages */}
                  <Route path="/deposit" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <DepositPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/bots" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <BotPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  
                  {/* ✅ Broker Integrations */}
                  <Route path="/broker-integrations" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <BrokerIntegrationsPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/broker/callback" element={<BrokerCallbackPage />} />
                  <Route path="/account-selection" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <AccountSelectionPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  
                  {/* ✅ Upgrade & Profile Pages */}
                  <Route path="/upgrade" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <UpgradePage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/profile" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <ProfilePage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  
                  {/* ✅ SchWeb (Your main integration page) */}
                  
                  
                  {/* ✅ Terms and Conditions (ของโบรกเกอร์ที่เลือก) */}
                  <Route path="/terms-and-conditions/:broker" element={<TermsAndConditions />} /> 
                  
                  {/* ✅ Account Linking (เชื่อมโยงบัญชีของโบรกเกอร์) */}
                 {/* <Route path="/account-linking/:broker" element={<AccountLinking />} /> */}
                  <Route path="/account" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <BrokerConnectionGuard>
                          <AccountPage />
                        </BrokerConnectionGuard>
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />

                  <Route path="/watchlist" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <WatchListPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/learn" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <LearnPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/trade-history" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <TradeHistoryPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />
                  <Route path="/notifications/history" element={
                    <ProtectedRoute>
                      <PaymentVerificationGuard>
                        <HistoryPage />
                      </PaymentVerificationGuard>
                    </ProtectedRoute>
                  } />

                  <Route path="/options-101" element={<Options101Page />} />

                  <Route path="/what-is-market-making" element={<WhatIsMarketMakingPage />} />
                  <Route path="/how-to-place-an-options-trade" element={<HowToPlaceAnOptionsTradePage />} />
                  <Route path="/position-size-and-risk-management" element={<PositionSizeAndRiskManagementPage />} />
                  <Route path="/pattern-day-trader-rule" element={<PatternDayTraderRulePage />} />
                  <Route path="/how-to-handle-options-assignment" element={<HowToHandleOptionsAssignmentPage />} />
                  <Route path="/disclaimer" element={<DisclaimerPage />} />
                  <Route path="/terms" element={<LegalTermsAndConditionsPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/refund-and-cancellation" element={<RefundAndCancellationPage />} />

                  <Route path="/options/what-are-options" element={<WhatAreOptions />} />
                  <Route path="/options/types-of-options" element={<TypesOfOptions />} />
                  <Route path="/options/options-strategies" element={<OptionsStrategies />} />
                  <Route path="/pages-sch" element={<MainPageSch />} />
                  <Route path="/quantum-flo-signals" element={<QuantumFloSignalsPage />} />

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <PaymentVerificationGuard>
                          <AdminPage />
                        </PaymentVerificationGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute>
                        <PaymentVerificationGuard>
                          <AdminDashboard />
                        </PaymentVerificationGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/client-trade-history"
                    element={
                      <ProtectedRoute>
                        <PaymentVerificationGuard>
                          <AdminClientTradeHistory />
                        </PaymentVerificationGuard>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/client-performance"
                    element={
                      <ProtectedRoute>
                        <PaymentVerificationGuard>
                          <AdminClientPerformance />
                        </PaymentVerificationGuard>
                      </ProtectedRoute>
                    }
                  />

                  {/* Maintenance & Error Pages */}
                  <Route path="/maintenance" element={<MaintenancePage />} />
                  <Route path="*" element={<NotFoundPage />} />

                  </Routes>
                </main>
                <Footer />
              </div>
            </Router>
          </NotificationProvider>
        </BrokerConnectionsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
